<script setup>
import {computed, onMounted, ref} from "vue";
import {useBookingStore} from "@/stores/booking";
import {useGuestsStore} from "@/stores/guests";
import CheckinGuestForm from "@/components/CheckinGuestForm.vue";
import CheckinForm from "@/components/CheckinForm.vue";
import AppIcon from "@/components/AppIcon.vue";
import {useToast} from "vue-toastification";
import {useI18n} from "vue-i18n";

const bookingStore = useBookingStore()
const guestsStore = useGuestsStore()
const toast = useToast()
const i18n = useI18n()

const isLoading = ref(false)
const loadFailed = ref(false)
const openIndex = ref(0)

const guestsAmount = computed(() => bookingStore.booking.guests.totalAmount)
const progressPct = computed(() => {
  if (!guestsAmount.value) {
    return 0
  }

  return Math.round((guestsStore.guestsValidAmount / guestsAmount.value) * 100)
})

function isValidGuest(guest) {
  return !!guest.hash
}

function toggle(index) {
  openIndex.value = openIndex.value === index ? null : index
}

function firstIncompleteIndex() {
  return guestsStore.guests.findIndex(guest => !isValidGuest(guest))
}

function onSaved(index) {
  if (!isValidGuest(guestsStore.guests[index])) {
    return
  }

  const next = firstIncompleteIndex()
  openIndex.value = next === -1 ? null : next
}

// Without this the guest silently gets a blank form and re-types guests that
// are already registered, so a failed load has to be visible and retryable.
function loadGuests() {
  isLoading.value = true
  loadFailed.value = false

  guestsStore
      .fetchGuests()
      .catch(err => {
        console.error(err)
        loadFailed.value = true
        toast.error(i18n.t('alerts.somethingWentWrong'))
      })
      .finally(() => {
        isLoading.value = false

        const next = firstIncompleteIndex()
        openIndex.value = next === -1 ? null : next
      })
}

onMounted(loadGuests)
</script>

<template>
  <div class="ww-screen">
    <h1 class="ww-title">{{ $t('checkin') }}</h1>

    <div class="checkin__progress-row">
      <span class="checkin__progress-label">
        {{ guestsStore.guestsValidAmount }}/{{ guestsAmount }} {{ $t('checkinPage.progress') }}
      </span>
      <span class="checkin__progress-pct">{{ progressPct }}%</span>
    </div>
    <div
        class="ww-progress"
        role="progressbar"
        :aria-valuenow="progressPct"
        aria-valuemin="0"
        aria-valuemax="100"
    >
      <div class="ww-progress__bar" :style="{width: progressPct + '%'}"></div>
    </div>

    <div v-if="isLoading" class="checkin__loading">
      <span class="ww-spinner"></span>
      {{ $t('loadingGuests') }}
    </div>

    <div v-else-if="loadFailed" class="ww-card ww-card--dashed checkin__error">
      <app-icon name="cloud" :size="32" class="checkin__error-icon"/>
      <p class="checkin__error-text">{{ $t('alerts.guestsNotLoaded') }}</p>
      <button type="button" class="ww-btn ww-btn--primary ww-btn--sm" @click="loadGuests">
        {{ $t('retry') }}
      </button>
    </div>

    <template v-else>
      <div class="checkin__guests">
        <article
            v-for="(guest, index) in guestsStore.guests"
            :key="'guest-' + index"
            class="ww-card guest-card"
        >
          <button
              type="button"
              class="guest-card__head"
              :aria-expanded="openIndex === index"
              :aria-controls="'guest-panel-' + index"
              @click="toggle(index)"
          >
            <span class="guest-card__identity">
              <span class="guest-card__role">
                {{ index === 0 ? $t('checkinPage.roleMain') : $t('checkinPage.roleCo') }}
              </span>
              <span class="guest-card__name">
                {{
                  isValidGuest(guest)
                      ? guest.firstName + ' ' + guest.lastName
                      : $t('guests', 1) + ' ' + (index + 1)
                }}
              </span>
            </span>
            <span class="ww-pill" :class="{'ww-pill--done': isValidGuest(guest)}">
              {{ isValidGuest(guest) ? $t('checkinPage.complete') : $t('checkinPage.missing') }}
            </span>
            <app-icon
                :name="openIndex === index ? 'expand_less' : 'expand_more'"
                :size="22"
                class="guest-card__chevron"
            />
          </button>

          <div v-if="openIndex === index" :id="'guest-panel-' + index" class="guest-card__body ww-animate-grow">
            <checkin-guest-form
                :index="index"
                :id="'guest-' + index"
                @saved="onSaved(index)"
            />
          </div>
        </article>
      </div>

      <checkin-form/>
    </template>
  </div>
</template>

<style scoped>
.checkin__progress-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 9px;
}

.checkin__progress-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--ww-text-muted);
}

.checkin__progress-pct {
  font-size: 13px;
  font-weight: 700;
  color: var(--ww-primary);
}

.ww-progress {
  margin-top: 10px;
}

.checkin__loading {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 28px;
  font-size: 15px;
  font-weight: 600;
  color: var(--ww-text-muted);
}

.checkin__error {
  margin-top: 22px;
}

.checkin__error-icon {
  color: var(--ww-icon-muted);
  margin: 0 auto;
}

.checkin__error-text {
  margin: 12px 0 16px;
  font-size: 14px;
  line-height: 1.55;
  color: var(--ww-text-muted);
}

.checkin__guests {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 22px;
}

.guest-card {
  padding: 0;
  overflow: hidden;
}

.guest-card__head {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 18px;
  background: transparent;
  border: 0;
  text-align: left;
}

/* The header fills the card's padding box and the card clips, so the global
   focus ring has to be drawn inwards to stay visible. */
.guest-card__head:focus-visible {
  outline-offset: -3px;
}

.guest-card__identity {
  flex: 1 1 auto;
  min-width: 0;
}

.guest-card__role {
  display: block;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ww-text-label);
}

.guest-card__name {
  display: block;
  margin-top: 5px;
  font-size: 18px;
  font-weight: 700;
  color: var(--ww-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.guest-card__chevron {
  color: var(--ww-icon-muted);
}

.guest-card__body {
  padding: 2px 18px 20px;
}
</style>
